import React, { useState, useRef, useEffect } from 'react';

const StaffProfileDropdownWithFreeText = ({
  options = [],
  value,
  onChange,
  placeholder = "Select or type...",
  displayProperty = 'label',
  valueProperty = 'value',
  searchEnabled = true,
  className = '',
  style = {},
  disabled = false,
  label = '',
  labelClassName = '',
  dropdownClassName = '',
  optionClassName = '',
  searchPlaceholder = "Search or type...",
  noOptionsText = "No options found"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Update filtered options when options or searchTerm changes
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option =>
        option[displayProperty]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [options, searchTerm, displayProperty]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setIsTyping(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
        setIsTyping(false);
      }
    }
  };

  const handleOptionClick = (option) => {
    onChange(option[valueProperty]);
    setIsOpen(false);
    setSearchTerm('');
    setIsTyping(false);
  };

  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsTyping(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      e.preventDefault();
      // Add the typed value as a custom option
      onChange(searchTerm.trim());
      setIsOpen(false);
      setSearchTerm('');
      setIsTyping(false);
    }
  };

  const handleBlur = () => {
    if (searchTerm.trim() && isTyping) {
      // Add the typed value as a custom option when focus is lost
      onChange(searchTerm.trim());
      setSearchTerm('');
      setIsTyping(false);
    }
  };

  // Get display text for selected value
  const getDisplayText = () => {
    if (!value) return placeholder;
    const selectedOption = options.find(option => option[valueProperty] === value);
    return selectedOption ? selectedOption[displayProperty] : value;
  };

  // Prevent dropdown from closing when clicking on search input
  const handleSearchClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div className="mb-4">
      {label && (
        <label className={`block text-sm font-bold text-[#1A212F] mb-2 ${labelClassName}`}>
          {label}
        </label>
      )}
      
      {/* Dropdown Trigger */}
      <div
        ref={dropdownRef}
        className="relative"
      >
        <div
          className={`w-full px-3 py-2 bg-[#F8F9FB] border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-[14px] font-medium cursor-pointer flex items-center justify-between ${
            disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
          } ${className}`}
          onClick={handleToggle}
          style={{ 
            color: value ? '#1A212FB2' : '#1A212F66',
            ...style 
          }}
        >
                 <span className="truncate">{getDisplayText()}</span>
         <svg
           className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
             isOpen ? 'transform rotate-180' : ''
           }`}
           fill="none"
           stroke="currentColor"
           viewBox="0 0 24 24"
         >
           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
         </svg>
       </div>

       {/* Dropdown Menu */}
       {isOpen && (
         <div className={`absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden ${dropdownClassName}`}>
          {/* Search Input */}
          <div className="p-2 border-b border-gray-300">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onClick={handleSearchClick}
              placeholder={searchPlaceholder}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                {noOptionsText}
              </div>
            ) : (
              filteredOptions.map((option, index) => (
                <div
                  key={option[valueProperty] || index}
                  className={`px-3 py-2 text-[14px] font-medium cursor-pointer hover:bg-gray-50 transition-colors duration-150 ${
                    value === option[valueProperty] ? 'bg-blue-50 text-blue-600' : 'text-[#1A212FB2]'
                  } ${optionClassName}`}
                  onClick={() => handleOptionClick(option)}
                >
                  {option[displayProperty]}
                </div>
              ))
            )}
                     </div>
         </div>
       )}
       </div>
     </div>
   );
 };

export default StaffProfileDropdownWithFreeText; 